// src/services/referralService.js
import api from './apiClient';
import { track } from '../utils/dataLayer';

// The backend answers most records unwrapped; an enveloped `{ data }` is read too.
const unwrap = (body) =>
  body?.data && typeof body.data === 'object' && !Array.isArray(body.data)
    ? body.data
    : body;

export const REFERRAL_SHIRT_SHOP_URL = 'https://shop.rxrooster.com';
export const REFERRAL_APPLIED_EVENT = 'rxrooster:referral-applied';

/**
 * Build MyDesigns' coupon share link without trusting a shop destination or
 * exposing the private code anywhere except the outbound URL.
 */
export function referralShirtCheckoutUrl(
  couponCode,
  destination = REFERRAL_SHIRT_SHOP_URL,
) {
  if (!/^[A-Za-z0-9_-]{3,128}$/.test(couponCode || '')) return null;
  try {
    const url = new URL(destination);
    if (
      url.origin !== REFERRAL_SHIRT_SHOP_URL ||
      url.username ||
      url.password
    ) return null;
    url.searchParams.set('code', couponCode);
    url.hash = '';
    return url.href;
  } catch {
    return null;
  }
}

const INVITER_REWARD_POLICIES = [
  'referral_lead_services_v5',
  'referral_invite_attestation_v4',
  'referral_profile_completion_v3',
  'referral_registration_v2',
];
const PROGRAM_REQUIREMENTS = [
  'inviter_and_referred_published_services',
  'provider_invite_attestation',
  'registered_referral_and_completed_referred_profile',
  'completed_provider_setup_and_registered_referral',
];

export function referralShirtProgramVersion(program) {
  if (
    program?.policy === 'referral_lead_services_v5' &&
    program?.requires === 'inviter_and_referred_published_services'
  ) return 'v5';
  if (
    program?.policy === 'referral_invite_attestation_v4' &&
    program?.requires === 'provider_invite_attestation'
  ) return 'v4';
  if (
    program?.policy === 'referral_profile_completion_v3' &&
    program?.requires === 'registered_referral_and_completed_referred_profile'
  ) return 'v3';
  if (
    program?.policy === 'referral_registration_v2' &&
    program?.requires === 'completed_provider_setup_and_registered_referral'
  ) return 'v2';
  return null;
}

export const normalizeGiftDesignId = (value) =>
  /^(?:shop-product-\d{1,20}|shirt-slot-[1-4])$/.test(String(value || ''))
    ? String(value)
    : null;

const shopProductUrl = (value) => {
  try {
    const url = new URL(value);
    return url.origin === REFERRAL_SHIRT_SHOP_URL && !url.username && !url.password
      ? url.href
      : null;
  } catch {
    return null;
  }
};

const normalizeDesign = (design) => {
  const id = normalizeGiftDesignId(design?.id);
  if (!id) return null;
  const productUrl = shopProductUrl(design.productUrl);
  let imageUrl = null;
  try {
    const image = new URL(design.imageUrl);
    if (image.protocol === 'https:' && !image.username && !image.password)
      imageUrl = image.href;
  } catch { /* A historical preview selection may have no product image. */ }
  return {
    id,
    label: String(design.label || 'Selected shirt').slice(0, 120),
    configured: design.configured === true && !!productUrl && !!imageUrl,
    // Legacy issued reward snapshots may omit current shop availability.
    // Missing data must not be presented as a confirmed stock outage.
    available: typeof design.available === 'boolean' ? design.available : null,
    eligible: design.eligible === true,
    productUrl,
    imageUrl,
  };
};

export function giftInvitationUrl(inviteUrl, designId) {
  const id = normalizeGiftDesignId(designId);
  if (!inviteUrl || !id) return null;
  try {
    const url = new URL(inviteUrl);
    if (!['http:', 'https:'].includes(url.protocol)) return null;
    url.searchParams.set('gift', id);
    return url.href;
  } catch {
    return null;
  }
}

/** Active offers can earn a gift before full-cost shop coupons are ready. */
export async function getShirtReferralProgram() {
  const data = unwrap(await api.get('/referrals/shirt-program'));
  const ids = new Set();
  const designs = (Array.isArray(data?.designs) ? data.designs : [])
    .map(normalizeDesign)
    .filter((design) => {
      // Historical IDs must remain resolvable even when they share a product
      // with a current catalog record. The picker deduplicates eligible products.
      if (!design || ids.has(design.id)) return false;
      ids.add(design.id);
      return true;
    });
  const policy = INVITER_REWARD_POLICIES.includes(data?.policy) ? data.policy : null;
  const requires = PROGRAM_REQUIREMENTS.includes(data?.requires) ? data.requires : null;
  const programVersion = referralShirtProgramVersion({ policy, requires });
  // v4 intentionally has no pre-claim catalog: shirt, size and color are
  // chosen only after the authenticated shop handoff. Older programs still
  // need the in-app catalog that their selected-design links promise.
  const enabled = data?.enabled === true && !!programVersion &&
    (['v4', 'v5'].includes(programVersion) || data?.catalogReady === true);
  const publicDesigns = ['v4', 'v5'].includes(programVersion) ? [] : designs;
  return {
    enabled,
    policy,
    recipient: data?.recipient === 'referring_provider' ? data.recipient : null,
    // Preserve both published contracts during a staggered deployment. UI
    // copy uses the matching policy/requirement pair and fails closed otherwise.
    requires,
    fulfillmentMode: !enabled ? 'preview' : data?.fulfillmentMode === 'in_app' ? 'in_app' : 'email_pending',
    includesShipping: data?.includesShipping === true,
    allCostsCovered: data?.allCostsCovered === true,
    shopUrl: REFERRAL_SHIRT_SHOP_URL,
    preview: !enabled,
    designs: publicDesigns,
    featuredDesignIds: Array.isArray(data?.featuredDesignIds)
      ? Array.from(new Set(data.featuredDesignIds.map(normalizeGiftDesignId).filter(Boolean)))
      : [],
    catalogReady: data?.catalogReady === true,
    catalog: data?.catalog && typeof data.catalog === 'object' ? {
      sourceUrl: data.catalog.sourceUrl === `${REFERRAL_SHIRT_SHOP_URL}/meta-feed.csv` ? data.catalog.sourceUrl : null,
      fetchedAt: typeof data.catalog.fetchedAt === 'string' ? data.catalog.fetchedAt : null,
      feedSha256: /^[a-f0-9]{64}$/.test(data.catalog.feedSha256 || '') ? data.catalog.feedSha256 : null,
      version: typeof data.catalog.version === 'string' ? data.catalog.version : null,
      eligibleDesignCount: new Set(publicDesigns.filter((design) => design.configured && design.available && design.eligible).map((design) => design.productUrl)).size,
      reason: typeof data.catalog.reason === 'string' ? data.catalog.reason.slice(0, 160) : null,
    } : null,
    selectedDesign: ['v4', 'v5'].includes(programVersion)
      ? null
      : normalizeDesign(data?.selectedDesign),
  };
}

const normalizeShirtReward = (body) => {
  const data = unwrap(body);
  const rawRewardPolicy = typeof data?.rewardPolicy === 'string'
    ? data.rewardPolicy
    : null;
  const rewardPolicy = [...INVITER_REWARD_POLICIES, 'recipient_legacy_v1'].includes(data?.rewardPolicy)
    ? data.rewardPolicy
    : null;
  let status = ['none', 'referral_required', 'onboarding', 'preview', 'waiting', 'ready', 'expired'].includes(data?.status)
    ? data.status
    : 'none';
  const expiresAt = Date.parse(data?.expiresAt || '');
  if (
    status === 'ready' &&
    Number.isFinite(expiresAt) &&
    expiresAt <= Date.now()
  ) {
    status = 'expired';
  }
  const couponCode =
    status === 'ready' && /^[A-Za-z0-9_-]{3,128}$/.test(data?.couponCode || '')
      ? data.couponCode
      : null;
  // An incomplete answer must never look like a redeemable coupon.
  if (status === 'ready' && !couponCode) status = 'waiting';
  const referralProgress = [
    'not_started',
    'registered_profile_pending',
    'qualified',
  ].includes(data?.referralProgress)
    ? data.referralProgress
    // Preserve a truthful result during a staggered deploy without inventing
    // the profile-pending milestone from aggregate referral counts.
    : data?.referralQualified === true
      ? 'qualified'
      : 'not_started';
  const rawClaimState = [
    'attestation_required',
    'waiting',
    'ready',
    'unavailable',
  ].includes(data?.claimState)
    ? data.claimState
    : null;
  const inviteAttested = data?.inviteAttested === true;
  const claimState = rewardPolicy !== 'referral_invite_attestation_v4'
    ? null
    : rawClaimState === 'attestation_required' && !inviteAttested && status === 'referral_required'
      ? rawClaimState
      : rawClaimState === 'waiting' && inviteAttested && status === 'waiting'
        ? rawClaimState
        : rawClaimState === 'ready' && inviteAttested && status === 'ready'
          ? rawClaimState
        : rawClaimState === 'unavailable' && status === 'none'
          ? rawClaimState
          : 'unavailable';
  const inviteAttestedAt = Date.parse(data?.inviteAttestedAt || '');
  const shippingQualifiedAt = typeof data?.shippingQualifiedAt === 'string'
    ? Date.parse(data.shippingQualifiedAt)
    : Number.NaN;
  const shippingEligibility = rewardPolicy === 'referral_invite_attestation_v4'
    ? data?.shippingEligibility === 'eligible_for_manual_approval' &&
      Number.isFinite(shippingQualifiedAt)
      ? 'eligible_for_manual_approval'
      : 'awaiting_referred_registration'
    : null;
  const v5States = [
    'setup_required',
    'share_ready',
    'awaiting_referred_registration',
    'awaiting_referred_service',
    'preparing',
    'ready',
    'unavailable',
  ];
  const pilotEligible = data?.pilotEligible === true;
  const rawCampaignState = v5States.includes(data?.campaignState)
    ? data.campaignState
    : null;
  // V5 is a server-verified milestone contract. A partial or contradictory
  // response fails closed instead of letting stale fields advance the UI.
  const inviterServicePublished = pilotEligible && data?.inviterServicePublished === true;
  const referredRegistered = inviterServicePublished && data?.referredRegistered === true;
  const referredServicePublished = referredRegistered && data?.referredServicePublished === true;
  const qualifiedAt = Date.parse(data?.qualifiedAt || '');
  const v5CouponCoversAllCosts =
    data?.includesShipping === true && data?.allCostsCovered === true;
  const milestoneStateValid =
    (rawCampaignState === 'setup_required' && status === 'onboarding' && !inviterServicePublished) ||
    (rawCampaignState === 'share_ready' && status === 'referral_required' && inviterServicePublished && !referredRegistered) ||
    (rawCampaignState === 'awaiting_referred_registration' && status === 'referral_required' && inviterServicePublished && !referredRegistered) ||
    (rawCampaignState === 'awaiting_referred_service' && status === 'referral_required' && referredRegistered && !referredServicePublished) ||
    (rawCampaignState === 'preparing' && status === 'waiting' && referredServicePublished && Number.isFinite(qualifiedAt)) ||
    (rawCampaignState === 'ready' && status === 'ready' && referredServicePublished && Number.isFinite(qualifiedAt)) ||
    (rawCampaignState === 'unavailable' && ['none', 'expired'].includes(status));
  const v5Ready =
    rewardPolicy === 'referral_lead_services_v5' &&
    pilotEligible &&
    rawCampaignState === 'ready' &&
    milestoneStateValid &&
    status === 'ready' &&
    !!couponCode &&
    v5CouponCoversAllCosts;
  // A V5 coupon is not redeemable until the response proves that the shirt,
  // shipping, taxes and required fees are covered. Treat a partial ready
  // response like the same preparing state used while inventory is assigned.
  if (
    rewardPolicy === 'referral_lead_services_v5' &&
    rawCampaignState === 'ready' &&
    status === 'ready' &&
    !v5Ready
  ) status = 'waiting';
  const campaignState = rewardPolicy !== 'referral_lead_services_v5'
    ? null
    : !pilotEligible || !milestoneStateValid
      ? 'unavailable'
      : status === 'expired'
        ? 'unavailable'
      : rawCampaignState === 'ready' && !v5Ready
        ? 'preparing'
        : rawCampaignState || 'unavailable';
  return {
    status,
    rewardSource: ['referred', 'inviter'].includes(data?.rewardSource) ? data.rewardSource : null,
    rewardPolicy,
    policyUnknown: !!rawRewardPolicy && !rewardPolicy,
    referralRequired: data?.referralRequired === true,
    referralQualified: data?.referralQualified === true,
    referralProgress,
    claimState,
    inviteAttested,
    inviteAttestedAt: inviteAttested && Number.isFinite(inviteAttestedAt)
      ? new Date(inviteAttestedAt).toISOString()
      : null,
    shippingEligibility,
    shippingQualifiedAt: shippingEligibility === 'eligible_for_manual_approval' &&
      Number.isFinite(shippingQualifiedAt)
      ? new Date(shippingQualifiedAt).toISOString()
      : null,
    checkoutAvailable: claimState === 'ready' && data?.checkoutAvailable === true,
    fulfillmentMode: ['none', 'preview'].includes(status)
      ? 'preview'
      : status === 'ready' || status === 'expired'
        ? 'in_app'
        : data?.fulfillmentMode === 'in_app' ? 'in_app' : 'email_pending',
    couponCode: status === 'ready' &&
      (rewardPolicy !== 'referral_lead_services_v5' || v5Ready)
      ? couponCode
      : null,
    expiresAt: Number.isFinite(expiresAt)
      ? new Date(expiresAt).toISOString()
      : null,
    shopUrl: REFERRAL_SHIRT_SHOP_URL,
    includesShipping: data?.includesShipping === true,
    allCostsCovered: data?.allCostsCovered === true,
    design: normalizeDesign(data?.design),
    remainingSteps: Array.isArray(data?.remainingSteps)
      ? data.remainingSteps.filter((step) => typeof step === 'string')
      : [],
    setupComplete: data?.setupComplete === true,
    canGiftShirt: data?.canGiftShirt === true,
    pilotEligible,
    campaignState,
    inviterServicePublished,
    referredRegistered,
    referredServicePublished,
    qualifiedAt: referredServicePublished && Number.isFinite(qualifiedAt)
      ? new Date(qualifiedAt).toISOString()
      : null,
  };
};

/** These endpoints expose only the signed-in provider's own shirt eligibility. */
export async function getMyShirtReward() {
  return normalizeShirtReward(await api.get('/referrals/my-shirt-reward'));
}

export async function claimMyShirtReward() {
  return normalizeShirtReward(
    await api.post('/referrals/my-shirt-reward/claim'),
  );
}

/** Record the signed-in provider's explicit v4 invitation attestation. */
export async function attestMyShirtInvite() {
  return normalizeShirtReward(
    await api.post('/referrals/my-shirt-reward/attest-invite', {
      invitedAtLeastOneCrna: true,
    }),
  );
}

const checkoutHandoffUrl = (value) => {
  if (typeof value !== 'string' || !value) return null;
  try {
    const base = api.baseURL || (typeof window !== 'undefined' ? window.location.origin : undefined);
    const url = new URL(value);
    const apiOrigin = base ? new URL(base).origin : null;
    return ['http:', 'https:'].includes(url.protocol) && url.origin === apiOrigin &&
      !url.username && !url.password &&
      url.pathname === '/referrals/my-shirt-reward/checkout' &&
      !url.search && !url.hash
      ? url.href
      : null;
  } catch {
    return null;
  }
};

/** Mint a short-lived, opaque, one-time route that redirects to the RxRooster shop. */
export async function createMyShirtCheckoutHandoff() {
  // The backend binds its one-time route to a short-lived, HttpOnly cookie.
  // Explicit credentials are required because app.rxrooster.com and
  // api.rxrooster.com are different origins.
  const data = unwrap(await api.request('/referrals/my-shirt-reward/checkout-handoff', {
    method: 'POST',
    body: JSON.stringify({}),
    credentials: 'include',
  }));
  const url = checkoutHandoffUrl(data?.url);
  const expiresAt = Date.parse(data?.expiresAt || '');
  if (!url || !Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
    throw new Error('checkout_handoff_unavailable');
  }
  return { url, expiresAt: new Date(expiresAt).toISOString() };
}

/** An authenticated arrival, checked against persisted setup by the server. */
export async function recordReferralMatchesArrival() {
  return normalizeShirtReward(
    await api.post('/referrals/my-shirt-reward/matches-arrival'),
  );
}

export async function getAdminShirtProgram() {
  return unwrap(await api.get('/referrals/admin/shirt-program'));
}

export async function setAdminShirtProgramEnabled(enabled) {
  return unwrap(await api.patch('/referrals/admin/shirt-program', { enabled }));
}

export async function importAdminShirtCoupons(batch) {
  return unwrap(await api.post('/referrals/admin/shirt-coupons', batch));
}

/**
 * The signed-in provider's invite link and truthful joined count. The count
 * is credited signups, not clicks, messages sent, or published pages, so it is safe to
 * use as social proof in the provider's private sharing workspace.
 */
export async function getMyInviteSummary() {
  const data = unwrap(await api.get('/referrals/my-code'));
  const totalReferred = Number(data?.stats?.totalReferred);
  const joinedCount = Number(data?.stats?.joinedCount);
  const publishedCount = Number(data?.stats?.publishedCount);
  const safeTotal =
    Number.isFinite(totalReferred) && totalReferred > 0 ? totalReferred : 0;
  const safeJoined =
    Number.isFinite(joinedCount) && joinedCount >= 0 ? joinedCount : safeTotal;
  const safePublished =
    Number.isFinite(publishedCount) && publishedCount > 0
      ? Math.min(publishedCount, safeJoined)
      : 0;
  return {
    url:
      typeof data?.shareUrl === 'string' && data.shareUrl
        ? data.shareUrl
        : null,
    totalReferred: safeTotal,
    joinedCount: safeJoined,
    publishedCount: safePublished,
  };
}

/** Backwards-compatible link-only reader used outside the share workspace. */
export async function getMyInviteLink() {
  return (await getMyInviteSummary()).url;
}

// ── Pending invite code ──────────────────────────────────────────────────────
// ReferralLanding (/r/:code) keeps the code in localStorage so it survives the
// sign-up wizard and an OAuth round trip (sessionStorage carries the form, the
// query string is rewritten on the way back). Signup applies it once the
// account exists, and AuthContext applies it whenever a session starts, which
// drops it for an existing account.
//
// Seven days: a code left by someone who opened an invite and never signed up
// must not credit the next person to register in that browser weeks later.
const CODE_KEY = 'referralCode';
const EXPIRY_KEY = 'referralCodeExpiry';
const NAME_KEY = 'referrerName';
const DESIGN_KEY = 'referralGiftDesign';
const DESIGN_CODE_KEY = 'referralGiftCode';
export const REFERRAL_CODE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
let memoryReferral = null;

const normalizeReferralCode = (code) =>
  String(code || '')
    .trim()
    .toUpperCase();
const availableReferralStorages = () => {
  const storages = [];
  try {
    if (typeof localStorage !== 'undefined') storages.push(localStorage);
  } catch {
    // Storage access itself can throw in a restricted browser context.
  }
  try {
    if (typeof sessionStorage !== 'undefined') storages.push(sessionStorage);
  } catch {
    // The in-memory carrier still covers the current SPA visit.
  }
  return storages;
};

/**
 * The HTML bootstrap removes private invite codes from page_location before
 * GTM can load. This consumes its same-page fallback when browser storage is
 * unavailable. The session copy remains available for OAuth and reloads.
 */
export function consumeBootstrappedReferralCode() {
  if (typeof window === 'undefined') return null;
  const code = normalizeReferralCode(window.__RXR_REFERRAL_CODE__);
  try {
    delete window.__RXR_REFERRAL_CODE__;
  } catch {
    window.__RXR_REFERRAL_CODE__ = undefined;
  }
  return code || null;
}

export async function referralAnalyticsId(code) {
  const normalized = normalizeReferralCode(code);
  if (!normalized) return null;
  try {
    const subtle = typeof window !== 'undefined' ? window.crypto?.subtle : null;
    if (!subtle || typeof TextEncoder === 'undefined') return null;
    const digest = await subtle.digest(
      'SHA-256',
      new TextEncoder().encode(normalized),
    );
    const hex = Array.from(new Uint8Array(digest), (byte) =>
      byte.toString(16).padStart(2, '0'),
    ).join('');
    return `sha256_${hex}`;
  } catch {
    return null;
  }
}

export function storePendingReferral(
  code,
  referrerName = '',
  now = Date.now(),
  giftDesignId = null,
) {
  const normalized = normalizeReferralCode(code);
  if (!normalized) return false;
  const expiry = now + REFERRAL_CODE_TTL_MS;
  memoryReferral = {
    code: normalized,
    expiry,
    giftDesignId: normalizeGiftDesignId(giftDesignId),
  };
  let persisted = false;
  try {
    localStorage.setItem(CODE_KEY, normalized);
    localStorage.setItem(EXPIRY_KEY, String(expiry));
    localStorage.setItem(NAME_KEY, referrerName || '');
    if (memoryReferral.giftDesignId) {
      localStorage.setItem(DESIGN_KEY, memoryReferral.giftDesignId);
      localStorage.setItem(DESIGN_CODE_KEY, normalized);
    } else {
      localStorage.removeItem(DESIGN_KEY);
      localStorage.removeItem(DESIGN_CODE_KEY);
    }
    persisted = true;
  } catch {
    // Same-tab storage or the in-memory copy can still carry the invitation.
  }
  try {
    sessionStorage.setItem(CODE_KEY, normalized);
    sessionStorage.setItem(EXPIRY_KEY, String(expiry));
    sessionStorage.setItem(NAME_KEY, referrerName || '');
    if (memoryReferral.giftDesignId) {
      sessionStorage.setItem(DESIGN_KEY, memoryReferral.giftDesignId);
      sessionStorage.setItem(DESIGN_CODE_KEY, normalized);
    } else {
      sessionStorage.removeItem(DESIGN_KEY);
      sessionStorage.removeItem(DESIGN_CODE_KEY);
    }
    persisted = true;
  } catch {
    // The in-memory copy still carries this SPA session.
  }
  return persisted;
}

export function clearPendingReferral() {
  memoryReferral = null;
  for (const storage of availableReferralStorages()) {
    try {
      storage.removeItem(CODE_KEY);
      storage.removeItem(EXPIRY_KEY);
      storage.removeItem(NAME_KEY);
      storage.removeItem(DESIGN_KEY);
      storage.removeItem(DESIGN_CODE_KEY);
    } catch {
      // Nothing to clear.
    }
  }
}

/** The stored code, or null. An expired or undated code is dropped. */
export function readPendingReferralCode(now = Date.now()) {
  const candidates = [];
  for (const storage of availableReferralStorages()) {
    try {
      const code = (storage.getItem(CODE_KEY) || '').trim();
      if (code) {
        candidates.push({
          code: normalizeReferralCode(code),
          expiry: Number(storage.getItem(EXPIRY_KEY)),
        });
      }
    } catch {
      // Try the next carrier.
    }
  }
  if (memoryReferral?.code) candidates.push(memoryReferral);
  const live = candidates.find(
    ({ expiry }) => Number.isFinite(Number(expiry)) && Number(expiry) > now,
  );
  if (live) return normalizeReferralCode(live.code);
  if (candidates.length) {
    clearPendingReferral();
  }
  return null;
}

let applyInFlight = null;

export function readPendingGiftDesignId(now = Date.now(), invitationCode) {
  const code = invitationCode === undefined
    ? readPendingReferralCode(now)
    : normalizeReferralCode(invitationCode);
  if (!code) return null;
  for (const storage of availableReferralStorages()) {
    try {
      if (Number(storage.getItem(EXPIRY_KEY)) > now && storage.getItem(DESIGN_CODE_KEY) === code) {
        const id = normalizeGiftDesignId(storage.getItem(DESIGN_KEY));
        if (id) return id;
      }
    } catch { /* Try the same-session carrier. */ }
  }
  return memoryReferral?.code === code ? normalizeGiftDesignId(memoryReferral?.giftDesignId) : null;
}

// A failed call is retried in place, for a blip right after signup. After
// that the code stays stored and the next session start (AuthContext: a login
// or an app load) tries again, inside the backend's 24-hour window for a new
// account. apiClient drops the HTTP status, so every failure is retried; the
// backend answers every refusal with a 200, so a failure is never final.
export const APPLY_RETRY_DELAYS_MS = [2000, 8000];

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function postPendingReferral(referralCode, retryDelaysMs, giftDesignId) {
  for (let attempt = 0; ; attempt += 1) {
    try {
      const data = unwrap(await api.post('/referrals/apply', {
        referralCode,
        ...(giftDesignId ? { giftDesignId } : {}),
      }));
      clearPendingReferral();
      if (data?.applied === true) {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new Event(REFERRAL_APPLIED_EVENT));
        }
        const refCode = await referralAnalyticsId(referralCode);
        track('referral_credit_applied', {
          audience: 'provider',
          source: 'peer_invite',
          ...(refCode ? { ref_code: refCode } : {}),
        });
      }
      return { applied: data?.applied === true, reason: data?.reason || null };
    } catch {
      if (attempt >= retryDelaysMs.length) return null;
      await wait(retryDelaysMs[attempt]);
    }
  }
}

/**
 * Credit the colleague whose invite link brought this visitor here. Call it
 * once an account's token is stored. It never throws and is safe to fire and
 * forget, so a referral can never fail a signup or a login.
 *
 * Every answer from POST /referrals/apply is final (credited, or refused for a
 * reason a retry cannot change, such as an account older than a day), so the
 * stored code is dropped on any answer. A failure is retried (see
 * APPLY_RETRY_DELAYS_MS) and then keeps the code for the next session start.
 *
 * Resolves to `{ applied, reason }`, or null when there was nothing to apply
 * or every try failed. Concurrent calls share one request.
 */
export function applyPendingReferral({
  retryDelaysMs = APPLY_RETRY_DELAYS_MS,
  referralCode: explicitCode,
  giftDesignId: explicitDesign,
} = {}) {
  if (applyInFlight) return applyInFlight;
  const referralCode =
    normalizeReferralCode(explicitCode) || readPendingReferralCode();
  if (!referralCode) return Promise.resolve(null);
  const giftDesignId = normalizeGiftDesignId(explicitDesign) || readPendingGiftDesignId(Date.now(), referralCode);
  applyInFlight = postPendingReferral(referralCode, retryDelaysMs, giftDesignId).finally(
    () => {
      applyInFlight = null;
    },
  );
  return applyInFlight;
}

export const referralService = {
  // Get current user's referral code and stats
  async getMyReferralCode() {
    const response = await api.get('/referrals/my-code');
    return response.data;
  },

  // Generate or regenerate referral code
  async generateCode() {
    const response = await api.post('/referrals/generate-code');
    return response.data;
  },

  // Send email invites
  async sendEmailInvites(emails, message) {
    const response = await api.post('/referrals/invite/email', {
      emails,
      message,
    });
    return response.data;
  },

  // Validate a referral code (public)
  async validateCode(code) {
    const response = await api.get(`/referrals/validate/${code}`);
    return response.data;
  },

  // Get leaderboard (admin only)
  async getLeaderboard(limit = 10) {
    const response = await api.get('/referrals/admin/leaderboard', {
      params: { limit },
    });
    return response.data;
  },
};

export default referralService;
